import React, { useState, useMemo } from 'react';

const CATEGORY_LABELS = {
  phrase: { icon: '💬', label: 'Phrases' },
  color: { icon: '🎨', label: 'Colors' },
  size: { icon: '📏', label: 'Sizes' },
  clothing: { icon: '👕', label: 'Clothing' },
  greeting: { icon: '👋', label: 'Greetings' },
};

export default function VocabLog({ words = [], vocabWords = [], onClose, gameSettings = {}, gameMode }) {
  const ALL_TABS = [
    { id: 'marketplace', label: gameSettings.vocab_tab_marketplace || 'Marketplace', icon: '🏪', enabled: gameSettings.vocab_enabled_marketplace !== false },
    { id: 'missions', label: gameSettings.vocab_tab_missions || 'Missions', icon: '🎯', enabled: gameSettings.vocab_enabled_missions !== false },
  ];
  const TABS = ALL_TABS.filter(t => t.enabled);

  // Default to current game mode's tab, or first available tab
  const defaultTab = gameMode === 'free-market' ? 'marketplace' : 'missions';
  const [activeTab, setActiveTab] = useState(TABS.find(t => t.id === defaultTab) ? defaultTab : TABS[0]?.id || 'missions');
  const [filter, setFilter] = useState('all');

  const shouldShuffle = gameSettings.vocab_shuffle === true;
  const limit = gameSettings.vocab_limit || 8;

  // Stable shuffle seed per popup open (changes when modal re-opens)
  const [shuffleSeed] = useState(() => Math.random());

  // Admin vocab for current tab
  const adminForTab = vocabWords
    .filter(v => v.tab === activeTab)
    .map(v => ({ word: v.word, meaning: v.meaning, arabic: v.arabic || '', context: v.category || 'phrase', isAdmin: true }));

  // User-learned words for current tab (words without tab field default to missions)
  const learnedForTab = words.filter(w => {
    if (w.tab) return w.tab === activeTab;
    return activeTab === 'missions';
  });

  // Merge: mark admin words that user has also learned
  const learnedKeys = new Set(learnedForTab.map(w => `${w.word}|${w.context}`));
  const allMerged = [
    ...learnedForTab.map(w => ({ ...w, isLearned: true })),
    ...adminForTab
      .filter(v => !learnedKeys.has(`${v.word}|${v.context}`))
      .map(v => ({ ...v, isLearned: false })),
  ];

  // Apply shuffle + limit
  const merged = useMemo(() => {
    let result = [...allMerged];
    if (shouldShuffle) {
      // Fisher-Yates with seed
      for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(((shuffleSeed * 9301 + 49297) % 233280 / 233280 * (i + 1) + i * shuffleSeed) % (i + 1));
        [result[i], result[j]] = [result[j], result[i]];
      }
    }
    if (limit > 0 && result.length > limit) {
      result = result.slice(0, limit);
    }
    return result;
  }, [allMerged.length, activeTab, shouldShuffle, limit, shuffleSeed]); // eslint-disable-line

  // Category filter
  const categories = ['all', ...new Set(merged.map(w => w.context).filter(Boolean))];
  const filtered = filter === 'all' ? merged : merged.filter(w => w.context === filter);

  // Group by category
  const grouped = {};
  filtered.forEach(w => {
    const cat = w.context || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(w);
  });

  const learnedCount = merged.filter(w => w.isLearned).length;
  const totalCount = merged.length;

  // Reset filter when switching tabs
  const handleTabSwitch = (tabId) => {
    setActiveTab(tabId);
    setFilter('all');
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-3 z-[60] animate-modal-overlay" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[85vh] flex flex-col animate-modal-content" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-5 pb-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-bold text-gray-900 text-lg">{gameSettings.vocab_title || 'Words I Learned'}</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {totalCount > 0
                  ? `${learnedCount} / ${totalCount} words learned`
                  : (gameSettings.vocab_count || '{N} words learned').replace('{N}', learnedCount)}
              </p>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors">✕</button>
          </div>

          {/* Tabs — hide if only 1 tab enabled */}
          {TABS.length > 1 && <div className="flex gap-1.5">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabSwitch(tab.id)}
                className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#2D6A4F] text-white shadow-sm'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>}
        </div>

        {/* Category filter */}
        {categories.length > 2 && (
          <div className="px-5 pt-3 flex gap-1.5 overflow-x-auto flex-shrink-0">
            {categories.map(cat => {
              const info = CATEGORY_LABELS[cat] || { icon: '📝', label: cat };
              return (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    filter === cat ? 'bg-[#2D6A4F] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat === 'all' ? (gameSettings.vocab_all_filter || 'All') : `${info.icon} ${gameSettings[`vocab_cat_${cat}`] || info.label}`}
                </button>
              );
            })}
          </div>
        )}

        {/* Word list */}
        <div className="flex-1 overflow-y-auto p-5">
          {filtered.length === 0 ? (
            <div className="text-center py-10">
              <span className="text-4xl block mb-3">{gameSettings.vocab_empty_icon || '📖'}</span>
              <p className="text-gray-500 text-sm">{gameSettings.vocab_empty || 'No words yet'}</p>
              <p className="text-gray-400 text-xs mt-1">
                {activeTab === 'marketplace'
                  ? (gameSettings.vocab_empty_hint_mp || 'Trade and browse the marketplace to learn Darija!')
                  : (gameSettings.vocab_empty_hint || 'Shop, bargain, and explore to learn Darija!')}
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {Object.entries(grouped).map(([cat, catWords]) => {
                const info = CATEGORY_LABELS[cat] || { icon: '📝', label: cat };
                return (
                  <div key={cat}>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                      {info.icon} {gameSettings[`vocab_cat_${cat}`] || info.label}
                    </p>
                    <div className="space-y-1.5">
                      {catWords.map((w, i) => (
                        <div
                          key={`${w.word}-${i}`}
                          className={`flex items-center gap-3 rounded-xl px-3.5 py-2.5 ${
                            w.isLearned ? 'bg-gray-50' : 'bg-gray-50/50 opacity-50'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-700">{w.meaning}</p>
                            {!w.isLearned && (
                              <p className="text-[9px] text-gray-400 mt-0.5">{gameSettings.vocab_unlearned_hint || 'Play to learn!'}</p>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className={`text-sm font-bold ${w.isLearned ? 'text-[#2D6A4F]' : 'text-gray-400'}`}>{w.word}</p>
                            {w.arabic && <p className="text-xs text-gray-500" dir="rtl">{w.arabic}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
